(() => {
  const STORAGE_KEY = 'bm_vehicle_garage_v1';
  const IMAGE_CACHE_KEY = 'bm_generation_image_cache_v1';
  const FITMENT_SECTION_ID = 'bm-fitment-options';
  const VERSION = 1;
  const compatibilityResolvers = new Map();
  let memoryState = { version: VERSION, active: null };
  let garageDetailRequest = 0;

  const safeParse = (value) => { try { return JSON.parse(value); } catch (error) { return null; } };

  const readState = () => {
    try {
      const stored = safeParse(window.localStorage.getItem(STORAGE_KEY));
      if (stored && stored.version === VERSION) memoryState = stored;
    } catch (error) {}
    return memoryState;
  };

  const writeState = (nextState) => {
    memoryState = nextState;
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextState)); } catch (error) {}
  };

  const readImageCache = () => {
    try { return safeParse(window.localStorage.getItem(IMAGE_CACHE_KEY)) || {}; }
    catch (error) { return {}; }
  };

  const writeImageCache = (generationId, imageUrl) => {
    if (!generationId || !imageUrl) return;
    try {
      const cache = readImageCache();
      cache[String(generationId)] = imageUrl;
      const keys = Object.keys(cache);
      if (keys.length > 30) keys.slice(0, keys.length - 30).forEach((key) => delete cache[key]);
      window.localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(cache));
    } catch (error) {}
  };

  const cleanNode = (node) => {
    if (!node) return null;
    const cleaned = {
      id: String(node.id || ''),
      name: String(node.name || ''),
      url: String(node.url || '')
    };
    if (node.image) cleaned.image = String(node.image);
    if (node.logo) cleaned.logo = String(node.logo);
    if (node.wheel) cleaned.wheel = String(node.wheel);
    if (node.years) cleaned.years = String(node.years);
    if (node.codes) cleaned.codes = String(node.codes);
    return cleaned;
  };

  const normalizeVehicle = (vehicle) => {
    if (!vehicle?.generation?.id) return null;
    return {
      source: vehicle.source || 'internal-generation',
      brand: cleanNode(vehicle.brand),
      model: cleanNode(vehicle.model),
      generation: cleanNode(vehicle.generation),
      providerIds: vehicle.providerIds && typeof vehicle.providerIds === 'object' ? vehicle.providerIds : {},
      selectedAt: Number(vehicle.selectedAt || Date.now())
    };
  };

  const getActive = () => readState().active || null;

  const vehicleLabel = (vehicle) => {
    if (!vehicle) return '';
    const generation = shortGenerationName(vehicle);
    const years = String(vehicle?.generation?.years || '').trim() || inlineGenerationYears(vehicle);
    return [vehicle.brand?.name || '', vehicle.model?.name || '', generation, years].filter(Boolean).join(' ');
  };

  const vehicleTitle = (vehicle) => {
    if (!vehicle) return '';
    return [vehicle.brand?.name || '', vehicle.model?.name || ''].filter(Boolean).join(' ') || vehicleLabel(vehicle);
  };

  const normalizeYears = (yearFrom, yearTo) => {
    const from = String(yearFrom || '').trim();
    const to = String(yearTo || '').trim();
    if (!from) return '';
    return `${from}–${to || 'heden'}`;
  };

  const shortGenerationName = (vehicle) => {
    let value = String(vehicle?.generation?.name || '').trim();
    const modelName = String(vehicle?.model?.name || '').trim();
    if (modelName && value.toLocaleLowerCase('nl').startsWith(modelName.toLocaleLowerCase('nl'))) {
      value = value.slice(modelName.length).trim();
    }
    value = value
      .replace(/\s+vanaf\s+\d{4}\s*$/i, '')
      .replace(/\s+\d{4}\s*[–-]\s*(?:\d{4}|heden)\s*$/i, '')
      .trim();
    return value;
  };

  const inlineGenerationYears = (vehicle) => {
    const raw = String(vehicle?.generation?.name || '');
    const fromMatch = raw.match(/\bvanaf\s+(\d{4})\b/i);
    if (fromMatch) return `vanaf ${fromMatch[1]}`;
    const rangeMatch = raw.match(/\b(\d{4})\s*[–-]\s*(\d{4}|heden)\b/i);
    if (rangeMatch) return `${rangeMatch[1]}–${rangeMatch[2]}`;
    return '';
  };

  const vehicleMeta = (vehicle, showCodes = false) => {
    const parts = [];
    const generation = shortGenerationName(vehicle);
    const years = String(vehicle?.generation?.years || '').trim() || inlineGenerationYears(vehicle);
    const codes = String(vehicle?.generation?.codes || '').trim();
    if (generation) parts.push(generation);
    if (years && !parts.includes(years)) parts.push(years);
    if (showCodes && codes && !parts.includes(codes)) parts.push(codes);
    return parts.join(' · ');
  };

  const notify = () => {
    renderGarage();
    renderProductCompatibility();
    window.dispatchEvent(new CustomEvent('bm:vehicle:change', { detail: { vehicle: getActive() } }));
  };

  const setActive = (vehicle) => {
    const normalized = normalizeVehicle(vehicle);
    if (!normalized) return false;
    writeState({ version: VERSION, active: normalized });
    notify();
    return true;
  };

  const clear = () => {
    writeState({ version: VERSION, active: null });
    notify();
  };

  const mergeProviderIds = (providerIds) => {
    const active = getActive();
    if (!active || !providerIds || typeof providerIds !== 'object') return false;
    active.providerIds = { ...(active.providerIds || {}), ...providerIds };
    active.selectedAt = Date.now();
    return setActive(active);
  };

  const normalizePath = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      return url.pathname.replace(/\/$/, '') || '/';
    } catch (error) {
      return String(value).split('?')[0].split('#')[0].replace(/\/$/, '');
    }
  };

  compatibilityResolvers.set('internal-generation', (vehicle, context) => {
    if (!vehicle?.generation) return null;
    const ids = String(context.fitmentIds || '').split('|').map((value) => value.trim()).filter(Boolean);
    if (ids.length && vehicle.generation.id) return ids.includes(String(vehicle.generation.id));
    const urls = String(context.fitmentUrls || '').split('|').map((value) => normalizePath(value)).filter(Boolean);
    const activeUrl = normalizePath(vehicle.generation.url);
    if (!urls.length || !activeUrl) return null;
    return urls.includes(activeUrl);
  });

  const registerCompatibilityResolver = (source, resolver) => {
    if (!source || typeof resolver !== 'function') return false;
    compatibilityResolvers.set(source, resolver);
    renderProductCompatibility();
    return true;
  };

  const garageElement = () => document.querySelector('[data-bm-my-vehicle]');
  const rootUrl = () => garageElement()?.dataset.rootUrl || '/';

  const selectorUrl = () => {
    const root = rootUrl();
    return `${root}${root.endsWith('/') ? '' : '/'}#voertuigselector`;
  };

  const setGarageImage = (garage, vehicle, imageUrl = '') => {
    const media = garage?.querySelector('[data-bm-my-auto-media]');
    const image = garage?.querySelector('[data-bm-vehicle-image]');
    const fallback = garage?.querySelector('[data-bm-vehicle-image-fallback]');
    if (!media || !image || !fallback) return;

    const showImage = garage.dataset.bmMyAutoShowImage !== 'false';
    media.hidden = !showImage;
    if (!showImage) return;

    if (imageUrl) {
      image.alt = vehicleTitle(vehicle) || vehicleLabel(vehicle);
      image.hidden = true;
      fallback.hidden = false;
      image.onload = () => {
        image.hidden = false;
        fallback.hidden = true;
      };
      image.onerror = () => {
        image.hidden = true;
        fallback.hidden = false;
      };
      image.src = imageUrl;
      if (image.complete && image.naturalWidth > 0) {
        image.hidden = false;
        fallback.hidden = true;
      }
    } else {
      image.onload = null;
      image.onerror = null;
      image.removeAttribute('src');
      image.alt = '';
      image.hidden = true;
      fallback.hidden = false;
    }
  };

  const findGenerationPageImage = (doc) => {
    const image = doc?.querySelector('.bm-vehicle-hero__visual img:not(.bm-logo-fallback), .bm-generation-image img');
    return image?.getAttribute('src') || '';
  };

  const fetchGenerationDetails = async (vehicle) => {
    const generationId = String(vehicle?.generation?.id || '');
    if (!generationId) return {};

    const details = {
      image: String(vehicle?.generation?.image || readImageCache()[generationId] || ''),
      years: String(vehicle?.generation?.years || ''),
      codes: String(vehicle?.generation?.codes || '')
    };

    if (vehicle?.model?.url && (!details.image || !details.years || !details.codes)) {
      try {
        const endpoint = new URL(vehicle.model.url, window.location.origin);
        endpoint.searchParams.set('section_id', FITMENT_SECTION_ID);
        const response = await fetch(endpoint.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' }
        });
        if (response.ok) {
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const choice = [...doc.querySelectorAll('.bm-choice[data-choice="generation"]')]
            .find((item) => String(item.dataset.id || '') === generationId);
          if (choice) {
            details.image = details.image || choice.dataset.image || choice.querySelector('img')?.getAttribute('src') || '';
            details.years = details.years || normalizeYears(choice.dataset.yearFrom, choice.dataset.yearTo);
            details.codes = details.codes || String(choice.dataset.codes || '');
          }
        }
      } catch (error) {}
    }

    if (!details.image && vehicle?.generation?.url) {
      try {
        const target = new URL(vehicle.generation.url, window.location.origin);
        if (normalizePath(target.pathname) === normalizePath(window.location.pathname)) {
          details.image = findGenerationPageImage(document);
        } else {
          const response = await fetch(target.toString(), {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' }
          });
          if (response.ok) {
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            details.image = findGenerationPageImage(doc);
          }
        }
      } catch (error) {}
    }

    if (details.image) writeImageCache(generationId, details.image);
    return details;
  };

  const persistResolvedDetails = (vehicle, details) => {
    const current = getActive();
    if (!current || String(current.generation?.id || '') !== String(vehicle?.generation?.id || '')) return current;

    const enriched = {
      ...current,
      generation: {
        ...(current.generation || {}),
        image: details.image || current.generation?.image || '',
        years: details.years || current.generation?.years || '',
        codes: details.codes || current.generation?.codes || ''
      }
    };
    const normalized = normalizeVehicle(enriched);
    if (normalized) writeState({ version: VERSION, active: normalized });
    return normalized || current;
  };

  function renderGarage() {
    const garage = garageElement();
    if (!garage) return;

    const enabled = garage.dataset.bmMyAutoEnabled !== 'false';
    const hideEmpty = garage.dataset.bmMyAutoHideEmpty !== 'false';
    const title = garage.querySelector('[data-bm-vehicle-title]');
    const meta = garage.querySelector('[data-bm-vehicle-meta]');
    const stockLink = garage.querySelector('[data-bm-my-auto-stock-link]');
    const stockLabel = garage.querySelector('[data-bm-my-auto-stock-label]');
    const changeLink = garage.querySelector('[data-bm-vehicle-change-link]');
    const active = getActive();

    if (!enabled) {
      garage.hidden = true;
      return;
    }

    if (!active) {
      garage.classList.add('is-empty');
      garage.hidden = hideEmpty;
      if (title) title.textContent = garage.dataset.bmMyAutoEmptyTitle || 'Kies jouw auto';
      if (meta) meta.textContent = garage.dataset.bmMyAutoEmptyMeta || '';
      if (stockLink) stockLink.href = selectorUrl();
      if (stockLabel) stockLabel.textContent = garage.dataset.bmMyAutoEmptyActionLabel || 'Kies auto';
      if (changeLink) changeLink.hidden = true;
      setGarageImage(garage, null, '');
      return;
    }

    garage.classList.remove('is-empty');
    garage.hidden = false;
    if (changeLink) {
      changeLink.hidden = false;
      changeLink.href = selectorUrl();
    }
    if (stockLink) stockLink.href = active.generation?.url || '/collections/all';
    if (stockLabel) stockLabel.textContent = garage.dataset.bmMyAutoStockLabel || 'Bekijk passende velgen';
    if (title) title.textContent = vehicleTitle(active);

    const showMeta = garage.dataset.bmMyAutoShowMeta !== 'false';
    const showCodes = garage.dataset.bmMyAutoShowCodes === 'true';
    const fallbackMeta = garage.dataset.bmMyAutoFallbackMeta || '';
    if (meta) meta.textContent = showMeta ? (vehicleMeta(active, showCodes) || fallbackMeta) : '';

    const generationId = String(active.generation?.id || '');
    const cachedImage = active.generation?.image || readImageCache()[generationId] || '';
    setGarageImage(garage, active, cachedImage);

    const needsDetails = (garage.dataset.bmMyAutoShowImage !== 'false' && !cachedImage)
      || (showMeta && (!active.generation?.years || (showCodes && !active.generation?.codes)));
    if (!needsDetails) return;

    const requestId = ++garageDetailRequest;
    fetchGenerationDetails(active).then((details) => {
      if (requestId !== garageDetailRequest) return;
      const current = getActive();
      if (!current || String(current.generation?.id || '') !== generationId) return;
      const enriched = persistResolvedDetails(current, details);
      setGarageImage(garage, enriched, details.image || enriched?.generation?.image || '');
      if (meta && showMeta) meta.textContent = vehicleMeta(enriched, showCodes) || fallbackMeta;
    }).catch(() => {});
  }

  function createCompatibilityBlock() {
    const block = document.createElement('div');
    block.className = 'bm-vehicle-compatibility';
    block.setAttribute('data-bm-vehicle-compatibility', '');
    block.innerHTML = `
      <span class="bm-vehicle-compatibility__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 16h14M7 16l1-5h8l1 5M8.5 11l1-3h5l1 3M7 19h.01M17 19h.01" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
      <span class="bm-vehicle-compatibility__body"><strong data-bm-compat-title>Controleer passing voor jouw auto</strong><span data-bm-compat-detail>Kies je auto om deze set met jouw generatie te vergelijken.</span></span>
      <a data-bm-compat-action href="#voertuigselector">Kies auto</a>`;
    return block;
  }

  function renderProductCompatibility() {
    const explicitContext = document.querySelector('[data-bm-product-fitment-context]');
    const buybox = document.querySelector('.bm-buybox');
    if (!buybox) return;
    const fitmentLinks = [...buybox.querySelectorAll('.bm-fitment-chips a[href]')];
    if (!explicitContext && !fitmentLinks.length) return;
    let block = buybox.querySelector('[data-bm-vehicle-compatibility]');
    if (!block) {
      block = createCompatibilityBlock();
      const catalog = buybox.querySelector('.bm-catalog-fitment');
      if (catalog) catalog.insertAdjacentElement('afterend', block);
      else buybox.appendChild(block);
    }
    const title = block.querySelector('[data-bm-compat-title]');
    const detail = block.querySelector('[data-bm-compat-detail]');
    const action = block.querySelector('[data-bm-compat-action]');
    const active = getActive();
    const source = explicitContext?.dataset.source || 'internal-generation';
    const fitmentUrls = explicitContext?.dataset.fitmentUrls || fitmentLinks.map((link) => link.href).join('|');
    block.classList.remove('is-match', 'is-warning', 'is-neutral');
    if (action) action.href = selectorUrl();
    if (!active) {
      block.classList.add('is-neutral');
      if (title) title.textContent = 'Past deze set op jouw auto?';
      if (detail) detail.textContent = 'Selecteer merk, model en generatie om de passing te controleren.';
      if (action) action.textContent = 'Kies auto';
      return;
    }
    const resolver = compatibilityResolvers.get(source);
    const result = resolver ? resolver(active, {
      fitmentIds: explicitContext?.dataset.fitmentIds || '',
      fitmentUrls,
      productId: explicitContext?.dataset.productId || '',
      productType: explicitContext?.dataset.productType || 'Velgenset'
    }) : null;
    const label = vehicleLabel(active);
    if (result === true) {
      block.classList.add('is-match');
      if (title) title.textContent = 'Past volgens onze database op jouw auto';
      if (detail) detail.textContent = label;
      if (action) action.textContent = 'Wijzig';
    } else if (result === false) {
      block.classList.add('is-warning');
      if (title) title.textContent = 'Niet gekoppeld aan jouw gekozen generatie';
      if (detail) detail.textContent = `${label} · controleer de passing vóór bestelling.`;
      if (action) action.textContent = 'Wijzig';
    } else {
      block.classList.add('is-neutral');
      if (title) title.textContent = 'Passing nog niet automatisch bevestigd';
      if (detail) detail.textContent = `${label} · controleer productgegevens en uitvoering.`;
      if (action) action.textContent = 'Wijzig';
    }
  }

  const findChoice = (root, type, id) => !root || !id ? null : [...root.querySelectorAll(`.bm-choice[data-choice="${type}"]`)].find((choice) => String(choice.dataset.id || '') === String(id)) || null;

  const waitForChoice = (root, type, id, timeout = 5000) => new Promise((resolve) => {
    const immediate = findChoice(root, type, id);
    if (immediate) { resolve(immediate); return; }
    let settled = false;
    const observer = new MutationObserver(() => {
      const choice = findChoice(root, type, id);
      if (choice && !settled) { settled = true; observer.disconnect(); resolve(choice); }
    });
    observer.observe(root, { childList: true, subtree: true });
    window.setTimeout(() => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      resolve(null);
    }, timeout);
  });

  async function restoreSelector() {
    const active = getActive();
    const root = document.querySelector('.bm-fitment');
    if (!active || !root || root.dataset.bmGarageRestored === 'true') return;
    if (root.dataset.bmSavedVehicleView === 'true') { root.dataset.bmGarageRestored = 'true'; return; }
    root.dataset.bmGarageRestored = 'loading';
    const brandChoice = findChoice(root, 'brand', active.brand?.id);
    if (!brandChoice) { root.dataset.bmGarageRestored = 'false'; return; }
    brandChoice.click();
    const modelChoice = await waitForChoice(root, 'model', active.model?.id);
    if (!modelChoice) { root.dataset.bmGarageRestored = 'false'; return; }
    modelChoice.click();
    const generationChoice = await waitForChoice(root, 'generation', active.generation?.id);
    if (!generationChoice) { root.dataset.bmGarageRestored = 'false'; return; }
    generationChoice.click();
    root.dataset.bmGarageRestored = 'true';
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-bm-vehicle-clear]')) clear();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    memoryState = { version: VERSION, active: null };
    readState();
    notify();
  });

  // Shopify Theme Editor replaces section markup without a full page reload.
  // Re-render from the central vehicle state so every editor setting is previewable immediately.
  document.addEventListener('shopify:section:load', () => {
    renderGarage();
    renderProductCompatibility();
  });

  window.BandenmeestersVehicle = {
    version: VERSION,
    storageKey: STORAGE_KEY,
    getActive,
    setActive,
    clear,
    mergeProviderIds,
    vehicleLabel,
    shortGenerationName,
    vehicleMeta,
    registerCompatibilityResolver
  };

  const init = () => {
    readState();
    renderGarage();
    renderProductCompatibility();
    restoreSelector();
    document.dispatchEvent(new CustomEvent('bm:vehicle:ready', { detail: { vehicle: getActive() } }));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();

// Product → vehicle selector → product return flow.
// The existing active vehicle is intentionally preserved while the shopper drafts another selection.
(() => {
  const RETURN_KEY = 'bm_vehicle_return_url';
  const currentPath = () => window.location.pathname + window.location.search;

  document.addEventListener('click', (event) => {
    if (!document.body.classList.contains('template-product')) return;
    const link = event.target.closest?.('a[href*="#voertuigselector"], [data-bm-product-vehicle-selector], [data-bm-compat-action]');
    if (!link) return;
    try { window.sessionStorage.setItem(RETURN_KEY, currentPath()); } catch (error) {}
  }, true);

  window.addEventListener('bm:vehicle:change', (event) => {
    if (!event.detail?.vehicle) return;
    let returnUrl = '';
    try { returnUrl = window.sessionStorage.getItem(RETURN_KEY) || ''; } catch (error) {}
    if (!returnUrl) return;
    try { window.sessionStorage.removeItem(RETURN_KEY); } catch (error) {}
    if (returnUrl !== currentPath()) window.setTimeout(() => window.location.assign(returnUrl), 70);
  });
})();
